import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoHead from "../components/seo/SeoHead";
import { useTranslation } from "../i18n";
import "./TestsHub.css";

const pageCopy = {
  es: {
    kicker: "TheQAHub Tests",
    title: "Tipos de tests con ejemplos reales",
    intro:
      "Una guia clara para entender el testing desde una clasificacion profesional, con ejemplos reales, lenguaje directo y criterio QA útil para estudiar, documentar mejor y tomar decisiones con más contexto en proyectos reales.",
    stats: [
      { value: "7", label: "familias de testing" },
      { value: "24", label: "ejemplos reales" },
      { value: "1", label: "1 caso por ficha" },
    ],
    guideTitle: "Como se explora esta biblioteca de tests",
    guide: [
      {
        title: "Clasificacion profesional",
        text: "Separamos los tests por nivel, objetivo, automatización, enfoque, fase, tipo de aplicación y especializacion, como lo haria un equipo real.",
      },
      {
        title: "Aterrizado a proyecto",
        text: "Cada ficha explica para que sirve el test, que caso de prueba ejecutaria un QA, que pasos seguiria y que bugs suele destapar.",
      },
      {
        title: "Pensado para enseñar",
        text: "La idea no es listar nombres. La idea es que un perfil junior o mid pueda entender cuando aplicar cada test y como defenderlo dentro del equipo.",
      },
    ],
    taxonomyTitle: "Clasificacion profesional de tipos de test",
    taxonomyIntro:
      "Esta es la base sobre la que se apoya el hub. No es una lista cerrada, pero si un marco serio y extensible para hablar de testing con precision.",
    families: [
      {
        name: "Tests por nivel",
        count: "5",
        description: "Define donde ocurre el test dentro del sistema.",
        items: ["Unit Testing", "Integration Testing", "System Testing", "Acceptance Testing", "UAT"],
      },
      {
        name: "Tests por objetivo",
        count: "6",
        description: "Explica que intentas validar realmente.",
        items: ["Functional", "Non-Functional", "Performance", "Security", "Usability", "Compatibility"],
      },
      {
        name: "Tests por automatización",
        count: "2",
        description: "Marca si el test lo ejecuta una persona o un sistema automatico.",
        items: ["Manual Testing", "Automated Testing"],
      },
      {
        name: "Tests por enfoque",
        count: "6",
        description: "Define desde que mirada atacas el problema.",
        items: ["Black Box", "White Box", "Gray Box", "Exploratory", "BDD", "TDD"],
      },
      {
        name: "Tests por momento",
        count: "4",
        description: "Aclara en que fase del ciclo tiene más sentido ejecutar la prueba.",
        items: ["Smoke", "Sanity", "Regression", "Release"],
      },
      {
        name: "Tests por aplicación",
        count: "5",
        description: "Aterriza el test segun el tipo de producto o superficie.",
        items: ["Web", "Mobile", "API", "Desktop", "Data"],
      },
      {
        name: "Tests especializados",
        count: "5",
        description: "Cubre necesidades más avanzadas y equipos más maduros.",
        items: ["Accessibility", "A/B", "Chaos", "Contract", "Reliability"],
      },
    ],
    playbookTitle: "Como documentamos un test en TheQAHub",
    playbook: [
      {
        title: "Tipo de test",
        text: "Nombramos la tecnica con claridad para que negocio, producto y QA hablen el mismo idioma.",
      },
      {
        title: "Objetivo",
        text: "Definimos que riesgo queremos cubrir y por que merece la pena ejecutar esa prueba.",
      },
      {
        title: "Caso de prueba",
        text: "Explicamos un escenario concreto y creible, no una definicion abstracta.",
      },
      {
        title: "Pasos y resultado esperado",
        text: "Describimos el recorrido de forma que se pueda repetir, revisar o automatizar despues.",
      },
      {
        title: "Posibles bugs",
        text: "Conectamos el test con los fallos que realmente ayuda a destapar en proyectos reales.",
      },
      {
        title: "Version automatizada",
        text: "Cuando aplica, enseñamos como se podria llevar a CI, a una suite o a una herramienta concreta.",
      },
    ],
    examplesTitle: "Ejemplos reales de testing",
    examplesIntro:
      "Cada ficha esta pensada para enseñar con contexto. Puedes filtrar por familia o por automatización para recorrer el hub con una lógica más cercana a como trabaja un equipo de QA.",
    filterFamily: "Familia",
    filterAutomation: "automatización",
    all: "Todas",
    allAutomation: "Todas",
    fields: {
      goal: "Objetivo",
      caseText: "Caso de prueba",
      expected: "Resultado esperado",
      bugs: "Posibles bugs",
      automated: "Version automatizada",
      focus: "Donde aporta más valor",
    },
    open: "Clicka encima para ver los detalles del test",
    modalClose: "Cerrar",
    modalLabels: {
      scenario: "Escenario de prueba",
      focus: "Lectura didactica",
      caseText: "Caso de prueba",
      steps: "Pasos",
      expected: "Resultado esperado",
      bugs: "Posibles bugs",
      automated: "Version automatizada",
      note: "Por que tiene sentido en un equipo real",
    },
    ctaTitle: "Esta biblioteca de tests esta pensada para crecer",
    ctaText:
      "Lo siguiente natural es convertir estas fichas en laboratorios más visuales, ejemplos automatizados y contenido enlazado con el diccionario de bugs para que teoria y practica se refuercen mutuamente.",
    ctaButton: "Ir a Bugs",
    suggestTitle: "Haz crecer Tests",
    suggestText:
      "Si quieres proponernos un tipo de test, un caso real o una mejora de clasificacion, puedes mandarlo como sugerencia para seguir ampliando la biblioteca.",
    suggestionLabel: "Tu idea de test o clasificacion",
    suggestionPlaceholder:
      "Ejemplo: Mutation testing con un caso real de lógica de descuentos o un laboratorio de contract testing entre microservicios...",
    sendMail: "Enviar sugerencia por email",
    contactCta: "Ir a contacto",
    mailSubject: "Sugerencia para TheQAHub Tests",
    mailFallback: "Quiero proponer una nueva entrada para TheQAHub Tests:",
  },
  en: {
    kicker: "TheQAHub Tests",
    title: "A didactic hub to understand testing through real examples",
    intro:
      "There is no closed list of test types, but there is a professional classification that works as a strong base and can keep growing. In this section we turn that structure into realistic examples, clear language and practical QA learning material.",
    stats: [
      { value: "7", label: "testing families" },
      { value: "24", label: "real examples" },
      { value: "1", label: "1 case per card" },
    ],
    guideTitle: "How to explore this test library",
    guide: [
      {
        title: "Professional classification",
        text: "We separate tests by level, objective, automation, approach, phase, application type and specialization, the way a real team would.",
      },
      {
        title: "Grounded in projects",
        text: "Each card explains what the test is for, which scenario a QA would run, which steps matter and which bugs it usually exposes.",
      },
      {
        title: "Built to teach",
        text: "The goal is not to list names. The goal is to help junior and mid-level QAs understand when to use each test and how to defend it.",
      },
    ],
    taxonomyTitle: "Professional classification of test types",
    taxonomyIntro:
      "This is the framework behind the hub. It is not a closed list, but it is a serious and extensible way to talk about testing with precision.",
    families: [
      {
        name: "Tests by level",
        count: "5",
        description: "Defines where the test happens inside the system.",
        items: ["Unit Testing", "Integration Testing", "System Testing", "Acceptance Testing", "UAT"],
      },
      {
        name: "Tests by objective",
        count: "6",
        description: "Explains what you are truly trying to validate.",
        items: ["Functional", "Non-Functional", "Performance", "Security", "Usability", "Compatibility"],
      },
      {
        name: "Tests by automation",
        count: "2",
        description: "Marks whether the test is run by a person or by automation.",
        items: ["Manual Testing", "Automated Testing"],
      },
      {
        name: "Tests by approach",
        count: "6",
        description: "Defines the lens used to attack the problem.",
        items: ["Black Box", "White Box", "Gray Box", "Exploratory", "BDD", "TDD"],
      },
      {
        name: "Tests by timing",
        count: "4",
        description: "Shows when in the lifecycle the test is most useful.",
        items: ["Smoke", "Sanity", "Regression", "Release"],
      },
      {
        name: "Tests by application",
        count: "5",
        description: "Grounds the test according to the product surface.",
        items: ["Web", "Mobile", "API", "Desktop", "Data"],
      },
      {
        name: "Specialized tests",
        count: "5",
        description: "Covers more advanced needs for more mature teams.",
        items: ["Accessibility", "A/B", "Chaos", "Contract", "Reliability"],
      },
    ],
    playbookTitle: "How we document a test at TheQAHub",
    playbook: [
      { title: "Test type", text: "We name the technique clearly so QA, product and business share the same language." },
      { title: "Goal", text: "We define which risk we want to cover and why the test matters." },
      { title: "Test case", text: "We describe a concrete and believable scenario, not an abstract definition." },
      { title: "Steps and expected result", text: "We make the flow reproducible, reviewable and easy to automate later." },
      { title: "Possible bugs", text: "We connect the test with the failures it really helps uncover." },
      { title: "Automated version", text: "When it makes sense, we show how it could move into CI, suites or tools." },
    ],
    examplesTitle: "Real testing examples",
    examplesIntro:
      "Each card is designed to teach with context. You can filter by family or automation level to explore the hub more like a real QA team would.",
    filterFamily: "Family",
    filterAutomation: "Automation",
    all: "All",
    allAutomation: "All",
    fields: {
      goal: "Goal",
      caseText: "Test case",
      expected: "Expected result",
      bugs: "Possible bugs",
      automated: "Automated version",
      focus: "Where it brings most value",
    },
    open: "Click above to view this test details",
    modalClose: "Close",
    modalLabels: {
      scenario: "Test scenario",
      focus: "Didactic reading",
      caseText: "Test case",
      steps: "Steps",
      expected: "Expected result",
      bugs: "Possible bugs",
      automated: "Automated version",
      note: "Why it makes sense in a real team",
    },
    ctaTitle: "This test library is built to grow",
    ctaText:
      "The natural next step is to turn these cards into more visual labs, automated examples and content linked with the bug dictionary so theory and practice reinforce each other.",
    ctaButton: "Go to Bugs",
    suggestTitle: "Help expand Test Types",
    suggestText:
      "If you want to suggest a test type, a realistic case or a better classification angle, you can send it so we keep growing the library.",
    suggestionLabel: "Your test or classification idea",
    suggestionPlaceholder:
      "Example: Mutation testing with a real pricing-rule scenario or a contract-testing lab between microservices...",
    sendMail: "Send suggestion by email",
    contactCta: "Go to contact",
    mailSubject: "Suggestion for TheQAHub Tests",
    mailFallback: "I want to propose a new entry for TheQAHub Tests:",
  },
  de: {
    kicker: "TheQAHub Tests",
    title: "Ein didaktischer Hub, um Testing uber reale Beispiele zu verstehen",
    intro:
      "Es gibt keine geschlossene Liste von Testarten, aber es gibt eine professionelle Klassifikation als starke Grundlage. In diesem Bereich machen wir daraus reale Beispiele, klare Sprache und nutzliches QA-Lernmaterial.",
    stats: [
      { value: "7", label: "Testing-Familien" },
      { value: "24", label: "reale Beispiele" },
      { value: "1", label: "1 Fall pro Karte" },
    ],
    guideTitle: "So erkundest du diese Testbibliothek",
    guide: [
      {
        title: "Professionelle Klassifikation",
        text: "Wir trennen Tests nach Ebene, Ziel, Automatisierung, Ansatz, Phase, Anwendungstyp und Spezialisierung, wie es ein reales Team tun wurde.",
      },
      {
        title: "Nah am Projekt",
        text: "Jede Karte erklart, wofur der Test gut ist, welches Szenario ein QA ausfuhren wurde und welche Bugs er oft aufdeckt.",
      },
      {
        title: "Zum Lernen gebaut",
        text: "Das Ziel ist nicht nur Namen zu listen, sondern Junior- und Mid-QAs zu helfen, Einsatz und Nutzen jedes Tests zu verstehen.",
      },
    ],
    taxonomyTitle: "Professionelle Klassifikation von Testarten",
    taxonomyIntro:
      "Das ist der Rahmen hinter dem Hub. Keine geschlossene Liste, aber eine seriose und erweiterbare Art, prazise uber Testing zu sprechen.",
    families: [
      {
        name: "Tests nach Ebene",
        count: "5",
        description: "Definiert, wo der Test im System stattfindet.",
        items: ["Unit Testing", "Integration Testing", "System Testing", "Acceptance Testing", "UAT"],
      },
      {
        name: "Tests nach Ziel",
        count: "6",
        description: "Erklart, was wirklich validiert werden soll.",
        items: ["Functional", "Non-Functional", "Performance", "Security", "Usability", "Compatibility"],
      },
      {
        name: "Tests nach Automatisierung",
        count: "2",
        description: "Zeigt, ob der Test von Menschen oder automatisch ausgefuhrt wird.",
        items: ["Manual Testing", "Automated Testing"],
      },
      {
        name: "Tests nach Ansatz",
        count: "6",
        description: "Definiert die Perspektive auf das Problem.",
        items: ["Black Box", "White Box", "Gray Box", "Exploratory", "BDD", "TDD"],
      },
      {
        name: "Tests nach Zeitpunkt",
        count: "4",
        description: "Zeigt, wann der Test im Lebenszyklus den grosten Wert liefert.",
        items: ["Smoke", "Sanity", "Regression", "Release"],
      },
      {
        name: "Tests nach Anwendung",
        count: "5",
        description: "Verankert den Test in der Produktoberflache.",
        items: ["Web", "Mobile", "API", "Desktop", "Data"],
      },
      {
        name: "Spezialisierte Tests",
        count: "5",
        description: "Deckt fortgeschrittenere Bedurfnisse reifer Teams ab.",
        items: ["Accessibility", "A/B", "Chaos", "Contract", "Reliability"],
      },
    ],
    playbookTitle: "Wie wir einen Test bei TheQAHub dokumentieren",
    playbook: [
      { title: "Testtyp", text: "Wir benennen die Technik klar, damit QA, Produkt und Business dieselbe Sprache sprechen." },
      { title: "Ziel", text: "Wir definieren, welches Risiko wir abdecken wollen und warum der Test relevant ist." },
      { title: "Testfall", text: "Wir beschreiben ein konkretes und glaubwurdiges Szenario statt einer abstrakten Definition." },
      { title: "Schritte und erwartetes Ergebnis", text: "So wird der Ablauf reproduzierbar, reviewbar und spater automatisierbar." },
      { title: "Mogliche Bugs", text: "Wir verbinden den Test mit realen Fehlertypen, die er aufdeckt." },
      { title: "Automatisierte Version", text: "Wenn sinnvoll, zeigen wir, wie der Test in CI, Suites oder Tools gehen konnte." },
    ],
    examplesTitle: "Reale Testbeispiele",
    examplesIntro:
      "Jede Karte soll mit Kontext lehren. Du kannst nach Familie oder Automatisierung filtern, um den Hub naher an echter QA-Arbeit zu erkunden.",
    filterFamily: "Familie",
    filterAutomation: "Automatisierung",
    all: "Alle",
    allAutomation: "Alle",
    fields: {
      goal: "Ziel",
      caseText: "Testfall",
      expected: "Erwartetes Ergebnis",
      bugs: "Mogliche Bugs",
      automated: "Automatisierte Version",
      focus: "Wo er am meisten Wert bringt",
    },
    open: "Klicke oben, um die Details dieses Tests zu sehen",
    modalClose: "Schliessen",
    modalLabels: {
      scenario: "Testszenario",
      focus: "Didaktische Lesart",
      caseText: "Testfall",
      steps: "Schritte",
      expected: "Erwartetes Ergebnis",
      bugs: "Mogliche Bugs",
      automated: "Automatisierte Version",
      note: "Warum das in einem realen Team sinnvoll ist",
    },
    ctaTitle: "Diese Testbibliothek ist fur Wachstum gebaut",
    ctaText:
      "Der naturliche nachste Schritt ist, diese Karten in visuellere Labs, automatisierte Beispiele und Inhalte zu verwandeln, die mit dem Bug Dictionary verknupft sind.",
    ctaButton: "Zu Bugs",
    suggestTitle: "Hilf mit, die Testarten zu erweitern",
    suggestText:
      "Wenn du eine Testart, einen realistischen Fall oder eine bessere Klassifikation vorschlagen willst, kannst du uns eine Idee senden.",
    suggestionLabel: "Deine Test- oder Klassifikationsidee",
    suggestionPlaceholder:
      "Beispiel: Mutation Testing mit echtem Rabattlogik-Fall oder ein Contract-Testing-Lab zwischen Microservices...",
    sendMail: "Vorschlag per E-Mail senden",
    contactCta: "Zum Kontakt",
    mailSubject: "Vorschlag fur TheQAHub Tests",
    mailFallback: "Ich mochte einen neuen Eintrag fur TheQAHub Tests vorschlagen:",
  },
};

const testExamples = [
  {
    id: "unit-tax",
    name: "Unit test de calculo fiscal",
    family: "Por nivel",
    automation: "Automatizado",
    tag: "Unit Testing",
    goal: "Verificar una funcion aislada que calcula subtotal, IVA y total sin depender de UI ni API.",
    caseText: "La funcion recibe 100 EUR, aplica 21 por ciento de IVA y devuelve subtotal, impuesto y total.",
    expected: "La formula responde igual siempre, redondea bien y cubre casos borde como 0 o decimales.",
    bugs: "Errores de redondeo, formulas tocadas por regresion o ramás no cubiertas en valores pequeños.",
    automated: "Jest o Vitest con una bateria corta de casos puros y rápidos.",
    focus: "Ideal cuando una regla de negocio vive en una funcion pequeña y necesitas feedback muy rápido.",
    steps: [
      "Ejecuta la funcion con 100 EUR.",
      "Comprueba subtotal, impuesto y total.",
      "Repite con 0 EUR y con valores decimales.",
    ],
    note: "Es uno de los tests más baratos de mantener y uno de los más utiles para proteger lógica pura.",
    surface: {
      title: "Funcion aislada",
      badge: "rápido y barato",
      rows: [
        { label: "Input", value: "subtotal: 100 EUR" },
        { label: "Regla", value: "IVA 21 por ciento" },
        { label: "Salida esperada", value: "subtotal 100 / impuesto 21 / total 121" },
      ],
    },
  },
  {
    id: "integration-login",
    name: "Integration test frontend + API de login",
    family: "Por nivel",
    automation: "Automatizado",
    tag: "Integration Testing",
    goal: "Validar que formulario, endpoint, cookies y estado de sesion trabajan juntos.",
    caseText: "El usuario envia credenciales validas desde la web y el backend devuelve sesion, rol y redireccion.",
    expected: "La UI persiste la sesion sin romper contrato ni estados intermedios.",
    bugs: "Campos mal mapeados, cookies no persistidas, contratos de respuesta inconsistentes.",
    automated: "Playwright o Cypress con entorno realista y trazas de red.",
    focus: "Aporta mucho cuando el riesgo esta en el cruce entre capas, no en una sola pieza aislada.",
    steps: [
      "Rellena email y password validos.",
      "Envia el formulario.",
      "Comprueba request, respuesta, cookie y redireccion al dashboard.",
    ],
    note: "Sirve para destapar problemás que no aparecen ni en unit tests puros ni en un test manual superficial.",
    surface: {
      title: "Cruce entre capas",
      badge: "Frontend + API",
      rows: [
        { label: "Request", value: "POST /login con credenciales validas" },
        { label: "Response", value: "200 con token, rol y expiracion" },
        { label: "Cliente", value: "sesion activa y dashboard visible" },
      ],
    },
  },
  {
    id: "system-login",
    name: "System test del flujo completo de login",
    family: "Por nivel",
    automation: "Automatizado",
    tag: "System Testing",
    goal: "Probar el sistema como un todo desde entrada publica hasta zona autenticada.",
    caseText: "Un usuario entra, inicia sesion, llega al dashboard y ve sus permisos basicos cargados.",
    expected: "Todo el sistema se comporta de forma coherente con UI, datos y servicios sincronizados.",
    bugs: "Redirecciones rotas, permisos mal reflejados, dashboards vacios tras login correcto.",
    automated: "Playwright o Cypress como flujo E2E priorizado.",
    focus: "Es el tipo de test que más se parece a lo que vive el usuario final cuando todo ya esta unido.",
    steps: [
      "Abre la pagina de login.",
      "Autenticate con un usuario valido.",
      "Valida dashboard, avatar y modulos basicos.",
    ],
    note: "Cuesta más que un unit o un integration, pero da contexto de negocio y experiencia real.",
    surface: {
      title: "Sistema completo",
      badge: "E2E",
      rows: [
        { label: "Inicio", value: "pantalla publica de login" },
        { label: "Transicion", value: "autenticacion correcta y carga de datos" },
        { label: "Fin", value: "dashboard utilizable con permisos correctos" },
      ],
    },
  },
  {
    id: "acceptance-checkout",
    name: "Acceptance test de aprobacion de checkout",
    family: "Por nivel",
    automation: "Manual",
    tag: "Acceptance Testing",
    goal: "Confirmar con negocio que el flujo final cumple lo que se pidio, no solo lo que se programo.",
    caseText: "Producto, cupon, impuestos, copy y estados finales coinciden con los criterios de aceptacion.",
    expected: "El flujo satisface la necesidad real del cliente o del producto.",
    bugs: "Reglas de negocio incompletas, mensajes finales ambiguos, impuestos o descuentos mal aterrizados.",
    automated: "Puede apoyarse en checks previos, pero el cierre suele requerir validacion humana.",
    focus: "Es clave cuando negocio necesita validar valor real y no solo exactitud tecnica.",
    steps: [
      "Ejecuta una compra representativa.",
      "Compara resultado con criterios de aceptacion.",
      "Valida con negocio o product owner.",
    ],
    note: "Es una prueba de confianza final: sirve para cerrar que lo construido resuelve el problema correcto.",
    surface: {
      title: "Validacion con negocio",
      badge: "Humano",
      rows: [
        { label: "Criterio", value: "checkout con cupón, impuestos y mensaje final correcto" },
        { label: "revisión", value: "producto y QA revisan juntos el resultado" },
        { label: "Decision", value: "aprobado o devuelto con feedback" },
      ],
    },
  },
  {
    id: "functional-signup",
    name: "Functional test de alta de usuario",
    family: "Por objetivo",
    automation: "Manual",
    tag: "Functional Testing",
    goal: "Verificar que una funcionalidad concreta hace exactamente lo que debe hacer.",
    caseText: "El formulario de alta crea una cuenta valida, usable y con el estado correcto.",
    expected: "El usuario se crea, recibe feedback claro y puede seguir el flujo previsto.",
    bugs: "Validaciones rotas, exito falso, persistencia incompleta o campos obligatorios ignorados.",
    automated: "Se puede automatizar despues como flujo repetible de formulario.",
    focus: "Muy útil para aterrizar historias de usuario y comprobar comportamientos esperados.",
    steps: [
      "Completa los campos obligatorios.",
      "Envia el formulario.",
      "Revisa mensaje final, email y estado creado.",
    ],
    note: "Es el tipo de prueba que más se asocia con el testing clasico de negocio.",
    surface: {
      title: "Alta funcional",
      badge: "Comportamiento esperado",
      rows: [
        { label: "Entrada", value: "nombre, email y password validos" },
        { label: "acción", value: "crear usuario" },
        { label: "Salida", value: "cuenta creada y disponible para login" },
      ],
    },
  },
  {
    id: "performance-dashboard",
    name: "Performance test de carga en dashboard",
    family: "Por objetivo",
    automation: "Automatizado",
    tag: "Performance Testing",
    goal: "Medir tiempo de respuesta y consumo cuando crece la concurrencia.",
    caseText: "El dashboard se consulta con 200 usuarios concurrentes mientras se observan tiempos y recursos.",
    expected: "La pantalla mantiene tiempos razonables y no degrada CPU, memoria o base de datos de forma anormal.",
    bugs: "Leaks, queries lentas, saturacion de CPU, colas acumuladas o timeouts.",
    automated: "k6, JMeter o Gatling apoyados por monitorizacion.",
    focus: "Es clave cuando el riesgo no es que algo falle siempre, sino que falle cuando el sistema se usa de verdad.",
    steps: [
      "Lanza carga concurrente controlada.",
      "Mide tiempo medio, percentiles y errores.",
      "Compara con umbrales aceptados por el equipo.",
    ],
    note: "En muchos productos el rendimiento es una funcionalidad silenciosa: cuando falla, el usuario lo percibe igual que un bug.",
    surface: {
      title: "Carga concurrente",
      badge: "200 usuarios",
      rows: [
        { label: "Objetivo", value: "p95 por debajo de 900 ms" },
        { label: "Lectura", value: "latencia, consumo y tasa de error" },
        { label: "Riesgo", value: "la app funciona, pero demasiado lenta bajo carga" },
      ],
    },
  },
  {
    id: "security-idor",
    name: "Security test de acceso no autorizado",
    family: "Por objetivo",
    automation: "Manual",
    tag: "Security Testing",
    goal: "Comprobar que un usuario no puede acceder a recursos de otro cambiando identificadores.",
    caseText: "Se modifica el ID en una URL privada para validar controles de autorizacion.",
    expected: "El sistema bloquea acceso, no expone datos y deja registro del intento.",
    bugs: "IDOR, permisos ausentes, fuga de datos o respuestas demasiado explicitas.",
    automated: "Puede evolucionar a colecciones API o checks de autorizacion automatizados.",
    focus: "Importa especialmente cuando la informacion privada viaja en rutas, query params o endpoints faciles de manipular.",
    steps: [
      "Accede con un usuario valido.",
      "Cambia el identificador del recurso en la URL o request.",
      "Comprueba si el sistema bloquea o expone informacion.",
    ],
    note: "No basta con que la UI oculte algo. El backend debe impedir el acceso real.",
    surface: {
      title: "Control de permisos",
      badge: "Riesgo alto",
      rows: [
        { label: "Usuario", value: "usuario A autenticado" },
        { label: "Manipulacion", value: "recurso de usuario B" },
        { label: "Resultado correcto", value: "403 o redireccion segura" },
      ],
    },
  },
  {
    id: "compatibility-safari",
    name: "Compatibility test de subida en Safari",
    family: "Por objetivo",
    automation: "Manual",
    tag: "Compatibility Testing",
    goal: "Comprobar si la misma acción funciona de forma consistente en distintos entornos soportados.",
    caseText: "La subida de archivo funciona en Chrome y se contrasta con Safari iOS en un dispositivo real.",
    expected: "El flujo se comporta igual o dentro de limites definidos por soporte.",
    bugs: "Eventos no soportados, estilos rotos, APIs del navegador incompatibles o permisos distintos.",
    automated: "Cross-browser runs y laboratorios de dispositivos cuando compensa.",
    focus: "Es especialmente valioso en superficies sensibles a navegador, viewport, sistema operativo o hardware.",
    steps: [
      "Ejecuta la subida de archivo en Chrome.",
      "Repite el mismo flujo en Safari iOS.",
      "Compara diferencias funcionales y visuales.",
    ],
    note: "Una funcionalidad puede estar perfecta en local y aun asi romperse donde el usuario real la usa.",
    surface: {
      title: "Comparativa de entorno",
      badge: "Cross-browser",
      rows: [
        { label: "Chrome", value: "subida correcta y previsualizacion visible" },
        { label: "Safari iOS", value: "selector abre pero no confirma archivo" },
        { label: "Decision", value: "bug de compatibilidad documentado con entorno exacto" },
      ],
    },
  },
  {
    id: "manual-exploratory",
    name: "Exploratory test de onboarding",
    family: "Por enfoque",
    automation: "Manual",
    tag: "Exploratory Testing",
    goal: "Descubrir riesgos, fricciones y estados raros no cubiertos por casos cerrados.",
    caseText: "Un QA recorre onboarding improvisando con criterio de usuario real y foco en zonas de riesgo.",
    expected: "Aparecen hallazgos que no estaban en el guion inicial: mensajes raros, perdidas de contexto o roces UX.",
    bugs: "Estados no previstos, combinaciones raras, errores blandos o copy engañoso.",
    automated: "No sustituye bien a la automatización; complementa y orienta nuevas suites.",
    focus: "Brilla cuando el producto cambia rápido o cuando sospechas que los casos cerrados no cuentan toda la historia.",
    steps: [
      "Recorre el onboarding sin un guion rigido.",
      "Prueba caminos felices y caminos raros.",
      "Anota hallazgos y nuevas hipotesis de riesgo.",
    ],
    note: "El valor no esta en improvisar por improvisar, sino en explorar con intencion y criterio.",
    surface: {
      title: "Exploracion guiada",
      badge: "Manual",
      rows: [
        { label: "Foco", value: "primeras impresiones, mensajes y cambios de contexto" },
        { label: "Mecanica", value: "observar, probar, aprender y reajustar" },
        { label: "Salida", value: "bugs y nuevas ideas de cobertura" },
      ],
    },
  },
  {
    id: "bdd-password",
    name: "BDD test de cambio de password",
    family: "Por enfoque",
    automation: "Automatizado",
    tag: "BDD",
    goal: "Describir el comportamiento en un lenguaje compartido por QA, negocio y desarrollo.",
    caseText: "Given usuario autenticado, When cambia password, Then recibe confirmación y la nueva clave funciona.",
    expected: "Todas las partes entienden el escenario igual y el sistema cumple ese comportamiento.",
    bugs: "Ambiguedad entre historia e implementacion, huecos de criterio o pasos desactualizados.",
    automated: "Cucumber o similares sobre UI o API.",
    focus: "Muy útil cuando necesitas traducir una regla de negocio a un lenguaje ejecutable y compartido.",
    steps: [
      "Define el escenario en Gherkin.",
      "Implementa los steps.",
      "Ejecuta el flujo y revisa que negocio lo sigue entendiendo.",
    ],
    note: "BDD aporta mucho cuando mejora la conversacion entre perfiles, no cuando se usa solo por moda.",
    surface: {
      title: "Lenguaje compartido",
      badge: "Given / When / Then",
      rows: [
        { label: "Given", value: "usuario autenticado en ajustes de cuenta" },
        { label: "When", value: "cambia password y confirma" },
        { label: "Then", value: "ve exito y la nueva clave funciona" },
      ],
    },
  },
  {
    id: "tdd-price-rule",
    name: "TDD de regla de descuento",
    family: "Por enfoque",
    automation: "Automatizado",
    tag: "TDD",
    goal: "Diseñar la lógica partiendo de un test que obliga a definir el comportamiento antes del codigo.",
    caseText: "Se escribe primero un test para una regla de descuento por volumen y despues se implementa la funcion minima.",
    expected: "La lógica nace alineada con el criterio y el test protege futuras regresiones.",
    bugs: "Reglas mal entendidas, implementaciones sobredimensionadas o huecos de cobertura inicial.",
    automated: "Jest, Vitest o el framework del lenguaje que toque.",
    focus: "Tiene valor cuando una regla de negocio es facil de romper y quieres obligarte a concretarla antes de programar.",
    steps: [
      "Escribe el test que falla.",
      "Implementa el codigo minimo para hacerlo pasar.",
      "Refactoriza manteniendo verde la suite.",
    ],
    note: "TDD no es solo una tecnica de codigo; tambien es una forma de pensar el comportamiento antes de construirlo.",
    surface: {
      title: "Ciclo rojo, verde, refactor",
      badge: "TDD",
      rows: [
        { label: "Rojo", value: "el test define la regla y falla" },
        { label: "Verde", value: "el codigo minimo la satisface" },
        { label: "Refactor", value: "se mejora sin perder comportamiento" },
      ],
    },
  },
  {
    id: "smoke-deploy",
    name: "Smoke test tras despliegue",
    family: "Por momento",
    automation: "Automatizado",
    tag: "Smoke Testing",
    goal: "Saber en pocos minutos si lo esencial del sistema sigue vivo tras un cambio.",
    caseText: "Se revisan login, home, dashboard y endpoint principal justo despues del despliegue.",
    expected: "Lo más importante arranca y permite seguir con más validaciones.",
    bugs: "caídas totales, despliegues incompletos, rutas core rotas o servicios caidos.",
    automated: "Suite corta en CI/CD o monitor de release.",
    focus: "Es una red de seguridad temprana: no profundiza mucho, pero evita perder tiempo en un sistema roto.",
    steps: [
      "Ejecuta la bateria minima tras desplegar.",
      "Comprueba servicios y pantallas core.",
      "Bloquea la release si algo esencial falla.",
    ],
    note: "El valor esta en la rapidez y en la capacidad de cortar una mala release cuanto antes.",
    surface: {
      title: "Chequeo minimo vital",
      badge: "Post-deploy",
      rows: [
        { label: "Cobertura", value: "home, login, dashboard y endpoint principal" },
        { label: "Tiempo", value: "minutos, no horas" },
        { label: "Decision", value: "seguir o bloquear release" },
      ],
    },
  },
  {
    id: "sanity-hotfix",
    name: "Sanity test de hotfix en perfil",
    family: "Por momento",
    automation: "Manual",
    tag: "Sanity Testing",
    goal: "Validar rápido que un cambio concreto ha quedado bien sin reabrir toda la aplicación.",
    caseText: "Se corrige un bug en la edicion de perfil y se revisa solo esa zona con combinaciones cercanas.",
    expected: "La correccion funciona y no deja incoherencias inmediatas alrededor.",
    bugs: "Fix incompleto, validaciones colaterales rotas, estados parciales mal actualizados.",
    automated: "A veces puede pasar a automatizado si el area falla mucho.",
    focus: "Es útil cuando no necesitas una regresion completa, sino confirmar que un parche puntual tiene sentido.",
    steps: [
      "Abre la pantalla afectada por el hotfix.",
      "Prueba el cambio principal y dos o tres variantes cercanas.",
      "Confirma que el area queda estable.",
    ],
    note: "Sanity no sustituye a regression. Sirve para recortar tiempo cuando el foco esta muy localizado.",
    surface: {
      title: "revisión focalizada",
      badge: "Cambio puntual",
      rows: [
        { label: "Hotfix", value: "guardar cambios de perfil sin error" },
        { label: "revisión", value: "nombre, avatar y confirmación visual" },
        { label: "Objetivo", value: "comprobar que el area reparada no sigue rota" },
      ],
    },
  },
  {
    id: "regression-checkout",
    name: "Regression suite de checkout",
    family: "Por momento",
    automation: "Automatizado",
    tag: "Regression Testing",
    goal: "Confirmar que cambios nuevos no rompen flujos ya aceptados previamente.",
    caseText: "Tras tocar precios y cupones se vuelve a ejecutar el checkout con variantes criticas.",
    expected: "Lo nuevo convive con lo anterior sin introducir regresiones colaterales.",
    bugs: "Totales rotos, mensajes desalineados, descuentos mal aplicados o pasos saltados.",
    automated: "Suites priorizadas en Cypress, Playwright o API tests.",
    focus: "Es esencial cuando una zona cambia mucho y tiene demasiado historial para confiar solo en revisiónes manuales.",
    steps: [
      "Selecciona escenarios regresivos clave.",
      "Ejecuta la suite sobre la version nueva.",
      "Compara con el baseline aceptado.",
    ],
    note: "Una buena regresion no es ejecutar todo siempre, sino cubrir lo que más duele si se rompe.",
    surface: {
      title: "Cobertura historica",
      badge: "Proteccion",
      rows: [
        { label: "Cambio reciente", value: "motor de precios y cupones" },
        { label: "Cobertura", value: "checkout feliz, con descuento y con error controlado" },
        { label: "Meta", value: "evitar romper lo que ya funcionaba" },
      ],
    },
  },
  {
    id: "release-mobile",
    name: "Release test en build mobile candidata",
    family: "Por momento",
    automation: "Manual",
    tag: "Release Testing",
    goal: "Tomar la decision final antes de publicar una build en un entorno realista.",
    caseText: "Se valida login, permisos, push, navegación y sensaciones finales en dispositivo real.",
    expected: "La build esta lista para salir sin defectos bloqueantes ni sorpresas visibles.",
    bugs: "Permisos mal gestionados, deep links fallidos, diferencias entre simulador y dispositivo real.",
    automated: "Puede apoyarse en smoke automatizado, pero la aprobacion final suele ser humana.",
    focus: "Tiene mucho peso cuando el coste de publicar mal es alto y necesitas una lectura final de producto.",
    steps: [
      "Instala la release candidate.",
      "Ejecuta flujos core en dispositivo real.",
      "Revisa sensaciones finales y errores visibles.",
    ],
    note: "No es solo comprobar que algo funciona; tambien es decidir si merece salir asi.",
    surface: {
      title: "Decision de salida",
      badge: "Release",
      rows: [
        { label: "Build", value: "candidata para publicacion" },
        { label: "Superficie", value: "dispositivo real y entorno controlado" },
        { label: "Decision", value: "publicar o devolver a correccion" },
      ],
    },
  },
  {
    id: "api-contract",
    name: "API test de contrato de pedidos",
    family: "Por aplicación",
    automation: "Automatizado",
    tag: "API Testing",
    goal: "Verificar que un endpoint critico mantiene contrato, schema y errores entendibles.",
    caseText: "POST /orders acepta el payload acordado y devuelve una respuesta estable con los campos comprometidos.",
    expected: "La API cumple el contrato y responde con consistencia tanto en exito como en error.",
    bugs: "400 inconsistentes, 500 no controlados, campos renombrados sin avisar o schema roto.",
    automated: "Postman/Newman, Pact, schemathesis o tests de contrato dedicados.",
    focus: "Aporta mucho cuando varias capas o servicios dependen del mismo contrato y una rotura propaga problemás.",
    steps: [
      "Envía un body valido.",
      "Valida status, schema y campos de respuesta.",
      "Prueba errores esperados con payload no valido.",
    ],
    note: "Una API puede responder 200 y aun asi romper un cliente si cambia el contrato sin control.",
    surface: {
      title: "Contrato de endpoint",
      badge: "API",
      rows: [
        { label: "Request", value: "POST /orders con items, direccion y pago" },
        { label: "Validacion", value: "status, schema, campos y mensajes de error" },
        { label: "Riesgo", value: "romper frontend o integraciones aguas abajo" },
      ],
    },
  },
  {
    id: "mobile-push",
    name: "Mobile test de permisos y push",
    family: "Por aplicación",
    automation: "Manual",
    tag: "Mobile Testing",
    goal: "Comprobar comportamiento real de permisos nativos y notificaciones push en app mobile.",
    caseText: "Se instala la app, se niegan permisos al principio y se recupera el flujo despues.",
    expected: "La app responde bien a aceptaciones, rechazos y cambios posteriores en ajustes.",
    bugs: "Bloqueos al denegar permisos, mensajes poco claros o push que no se registran.",
    automated: "Puede apoyarse en Appium, pero muchos matices siguen pidiendo validacion real.",
    focus: "Es especialmente útil cuando el sistema depende de permisos nativos o comportamientos propios del dispositivo.",
    steps: [
      "Instala la app en un dispositivo real.",
      "Rechaza permisos y comprueba el flujo.",
      "Activalos despues y revisa si la app se recupera bien.",
    ],
    note: "En mobile, muchas roturas serias aparecen solo fuera del simulador y con permisos reales de usuario.",
    surface: {
      title: "Permisos reales",
      badge: "Mobile",
      rows: [
        { label: "Primer arranque", value: "solicitud de permisos y decision del usuario" },
        { label: "Cambio", value: "activar permisos despues desde ajustes" },
        { label: "Validacion", value: "la app se adapta sin romper UX" },
      ],
    },
  },
  {
    id: "web-ui",
    name: "Web test de responsive en pricing",
    family: "Por aplicación",
    automation: "Manual",
    tag: "Web Testing",
    goal: "Revisar que una pagina publica mantiene jerarquia visual y claridad en varios viewports.",
    caseText: "La pagina de precios se valida en desktop, tablet y mobile con foco en CTA y bloques comparativos.",
    expected: "La jerarquia sigue clara y los elementos importantes no se cortan ni compiten entre si.",
    bugs: "CTA tapados, tarjetas desalineadas, textos cortados o scroll inesperado.",
    automated: "Visual regression y screenshots pueden apoyar, aunque no sustituyen del todo la lectura humana.",
    focus: "Es muy útil cuando la conversión depende de una presentación limpia y coherente en varios tamaños.",
    steps: [
      "Abre la pagina de precios en desktop.",
      "Repite en tablet y mobile.",
      "Compara CTA, textos y bloques clave.",
    ],
    note: "Muchas incidencias web no son binarias: la pagina carga, pero comunica peor y convierte menos.",
    surface: {
      title: "revisión de layout",
      badge: "Web",
      rows: [
        { label: "Desktop", value: "comparativa clara de planes" },
        { label: "Tablet", value: "componentes reordenados sin perdida de contexto" },
        { label: "Mobile", value: "CTA y precios visibles sin solapes" },
      ],
    },
  },
  {
    id: "accessibility-form",
    name: "Accessibility test de formulario critico",
    family: "Especializados",
    automation: "Manual",
    tag: "Accessibility Testing",
    goal: "Comprobar que un formulario sigue siendo usable con teclado, foco visible y lector de pantalla.",
    caseText: "El usuario recorre el formulario sin raton y valida labels, errores, contraste y orden de tabulacion.",
    expected: "La interfaz sigue siendo comprensible y operable para perfiles con distintas necesidades.",
    bugs: "Foco invisible, campos sin label, errores ambiguos o estructura semantica pobre.",
    automated: "axe, Lighthouse y checks WCAG ayudan, pero no sustituyen toda la revisión.",
    focus: "Es clave cuando el producto quiere ser usable de verdad y no limitar accesibilidad a un checklist superficial.",
    steps: [
      "Recorre la interfaz con teclado.",
      "Revisa el foco y el orden de tabulacion.",
      "Valida errores y labels con lector de pantalla.",
    ],
    note: "La accesibilidad bien entendida mejora la experiencia de muchas más personas de las que parece.",
    surface: {
      title: "Uso sin raton",
      badge: "Accesibilidad",
      rows: [
        { label: "Entrada", value: "teclado y lector de pantalla" },
        { label: "revisión", value: "foco, labels, contrastes y errores" },
        { label: "Meta", value: "que el flujo siga siendo comprensible y operable" },
      ],
    },
  },
  {
    id: "ab-pricing",
    name: "A/B test de hero en pricing",
    family: "Especializados",
    automation: "Automatizado",
    tag: "A/B Testing",
    goal: "Comparar dos versiones de una misma pantalla para medir cual ayuda mejor al objetivo de negocio.",
    caseText: "Se prueba una hero más directa frente a otra más explicativa en la pagina de precios.",
    expected: "La comparacion arroja una diferencia medible en conversión sin comprometer consistencia ni sesgos obvios.",
    bugs: "Eventos mal medidos, segmentacion incorrecta, variantes que rompen el tracking.",
    automated: "Plataformás de experimentacion, analitica y reglas de segmentacion.",
    focus: "Tiene valor cuando quieres validar hipotesis de producto con datos y no solo con intuicion.",
    steps: [
      "Define hipotesis y metrica principal.",
      "Lanza dos variantes controladas.",
      "Compara datos y comprueba calidad de tracking.",
    ],
    note: "No es un test de calidad clasico, pero si una tecnica muy valiosa para validar decisiones de producto.",
    surface: {
      title: "Comparativa controlada",
      badge: "Experimento",
      rows: [
        { label: "Variante A", value: "hero más descriptiva" },
        { label: "Variante B", value: "hero más directa y acciónable" },
        { label: "Lectura", value: "conversión, rebote y calidad del tracking" },
      ],
    },
  },
  {
    id: "chaos-payments",
    name: "Chaos test de caída del proveedor de pagos",
    family: "Especializados",
    automation: "Automatizado",
    tag: "Chaos Testing",
    goal: "Observar como degrada el sistema cuando una dependencia externa falla de verdad.",
    caseText: "Se interrumpe el proveedor de pagos en un entorno controlado y se mide la respuesta del checkout.",
    expected: "La app informa bien, conserva el carrito y evita colapsos o estados corruptos.",
    bugs: "Timeouts mal gestionados, cascadas de error, checkout bloqueado o perdida de datos de sesion.",
    automated: "Toxiproxy, feature flags, entornos de resiliencia y observabilidad.",
    focus: "Sirve para entrenar al sistema ante fallos reales, no solo ante caminos felices o errores domesticos.",
    steps: [
      "Corta la dependencia externa de forma controlada.",
      "Ejecuta el flujo afectado.",
      "Revisa degradacion, mensajes y capacidad de recuperacion.",
    ],
    note: "Chaos testing gana valor cuando el sistema depende de servicios externos criticos y quieres ver su madurez real.",
    surface: {
      title: "Resiliencia real",
      badge: "Dependencia externa",
      rows: [
        { label: "Fallo inducido", value: "proveedor de pagos no disponible" },
        { label: "Observacion", value: "mensajes, reintentos y consistencia del carrito" },
        { label: "Meta", value: "degradar con control y sin colapso" },
      ],
    },
  },
  {
    id: "data-migration",
    name: "Data test de migracion de usuarios",
    family: "Especializados",
    automation: "Automatizado",
    tag: "Data Testing",
    goal: "Validar que una migracion conserva conteos, relaciones y consistencia de negocio.",
    caseText: "Se migran usuarios, roles y preferencias desde una base antigua a otra nueva.",
    expected: "Los datos llegan completos, coherentes y utilizables tras el cambio.",
    bugs: "Perdida de registros, mapeos erroneos, relaciones rotas o duplicados silenciosos.",
    automated: "SQL checks, scripts de validacion y comparadores de datasets.",
    focus: "Importa mucho cuando el riesgo principal no esta en la UI, sino en la integridad de la informacion.",
    steps: [
      "Compara origen y destino.",
      "Valida conteos, claves y muestras criticas.",
      "Revisa relaciones y consistencia funcional.",
    ],
    note: "Muchos errores de datos no explotan al instante, pero rompen producto y confianza poco a poco.",
    surface: {
      title: "Integridad de datos",
      badge: "Data",
      rows: [
        { label: "Origen", value: "usuarios, roles y preferencias historicas" },
        { label: "revisión", value: "conteos, claves y relaciones" },
        { label: "Meta", value: "migracion utilizable y consistente" },
      ],
    },
  },
  {
    id: "contract-notifications",
    name: "Contract test entre pedidos y notificaciones",
    family: "Especializados",
    automation: "Automatizado",
    tag: "Contract Testing",
    goal: "Asegurar que dos servicios siguen compartiendo el mismo contrato sin romperse entre versiones.",
    caseText: "El servicio de pedidos emite un evento y el de notificaciones lo consume sin perder campos obligatorios.",
    expected: "Ambos servicios mantienen expectativas compatibles incluso cuando evolucionan por separado.",
    bugs: "Campos faltantes, tipos cambiados, versiones incompatibles o eventos consumidos a medias.",
    automated: "Pact u otras herramientas de consumer-driven contracts.",
    focus: "Es especialmente útil cuando varios equipos despliegan servicios independientes y un cambio pequeño puede romper a otro.",
    steps: [
      "Define expectativas del consumidor.",
      "Valida que el productor las satisface.",
      "Bloquea cambios incompatibles antes de desplegar.",
    ],
    note: "Ayuda a convertir integraciones fragiles en acuerdos verificables y versionados.",
    surface: {
      title: "Contrato entre servicios",
      badge: "Microservicios",
      rows: [
        { label: "Productor", value: "servicio de pedidos emite evento order.created" },
        { label: "Consumidor", value: "servicio de notificaciones espera campos obligatorios" },
        { label: "Riesgo", value: "romper integracion sin romper builds locales" },
      ],
    },
  },
  {
    id: "reliability-retries",
    name: "Reliability test de reintentos en colas",
    family: "Especializados",
    automation: "Automatizado",
    tag: "Reliability Testing",
    goal: "Validar que un sistema mantiene estabilidad y recuperacion correcta ante fallos transitorios.",
    caseText: "Una cola de procesamiento falla de forma intermitente y se comprueba si los reintentos respetan orden, limites y consistencia.",
    expected: "El sistema reintenta con control, evita duplicados y termina en un estado estable.",
    bugs: "Duplicados, reintentos infinitos, mensajes perdidos o estados finales incoherentes.",
    automated: "Tests de resiliencia, colas instrumentadas y observabilidad sobre reintentos.",
    focus: "Es especialmente útil cuando un proceso no siempre cae, pero su degradacion puede erosionar la confianza con el tiempo.",
    steps: [
      "Fuerza un fallo transitorio en el consumidor.",
      "Observa politicas de retry y backoff.",
      "Valida que no haya duplicados ni perdida de mensajes.",
    ],
    note: "La fiabilidad rara vez se percibe cuando funciona, pero su ausencia se nota enseguida en sistemás criticos.",
    surface: {
      title: "Estabilidad con reintentos",
      badge: "Reliability",
      rows: [
        { label: "Fallo", value: "error transitorio en el consumidor de cola" },
        { label: "Control", value: "retry con backoff y limite" },
        { label: "Meta", value: "consistencia final sin duplicados" },
      ],
    },
  },
];

const familyOptions = {
  es: [
    { value: "Por nivel", label: "Por nivel" },
    { value: "Por objetivo", label: "Por objetivo" },
    { value: "Por enfoque", label: "Por enfoque" },
    { value: "Por momento", label: "Por momento" },
    { value: "Por aplicación", label: "Por aplicación" },
    { value: "Especializados", label: "Especializados" },
  ],
  en: [
    { value: "Por nivel", label: "By level" },
    { value: "Por objetivo", label: "By objective" },
    { value: "Por enfoque", label: "By approach" },
    { value: "Por momento", label: "By timing" },
    { value: "Por aplicación", label: "By application" },
    { value: "Especializados", label: "Specialized" },
  ],
  de: [
    { value: "Por nivel", label: "Nach Ebene" },
    { value: "Por objetivo", label: "Nach Ziel" },
    { value: "Por enfoque", label: "Nach Ansatz" },
    { value: "Por momento", label: "Nach Zeitpunkt" },
    { value: "Por aplicación", label: "Nach Anwendung" },
    { value: "Especializados", label: "Spezialisiert" },
  ],
};

const automationOptions = {
  es: [
    { value: "Manual", label: "Manual" },
    { value: "Automatizado", label: "Automatizado" },
  ],
  en: [
    { value: "Manual", label: "Manual" },
    { value: "Automatizado", label: "Automated" },
  ],
  de: [
    { value: "Manual", label: "Manuell" },
    { value: "Automatizado", label: "Automatisiert" },
  ],
};

function TestExampleModal({ item, copy, onClose }) {
  return (
    <div className="tests-modal-overlay" onClick={onClose}>
      <div className="tests-modal" onClick={(event) => event.stopPropagation()}>
        <header className="tests-modal-header">
          <div>
            <div className="tests-modal-badges">
              <span className="tests-tag">{item.tag}</span>
              <span className="tests-automation">{item.automation}</span>
            </div>
            <h2>{item.name}</h2>
            <p>{item.goal}</p>
          </div>
          <button type="button" className="tests-close" onClick={onClose}>
            {copy.modalClose}
          </button>
        </header>

        <div className="tests-modal-body">
          <div className="tests-modal-grid">
            <section className="tests-surface-shell tests-modal-card">
              <p className="tests-section-kicker">{copy.modalLabels.scenario}</p>
              <div className="tests-surface-head">
                <strong>{item.surface.title}</strong>
                <span>{item.surface.badge}</span>
              </div>
              <div className="tests-surface-body">
                {item.surface.rows.map((row) => (
                  <div key={`${item.id}-${row.label}`} className="tests-surface-row">
                    <span>{row.label}</span>
                    <strong>{row.value}</strong>
                  </div>
                ))}
              </div>
            </section>

            <div className="tests-info-card tests-modal-card">
              <h3>{copy.modalLabels.caseText}</h3>
              <p>{item.caseText}</p>
            </div>

            <div className="tests-reading-card tests-modal-card">
              <h3>{copy.modalLabels.focus}</h3>
              <p>{item.focus}</p>
            </div>

            <div className="tests-info-card tests-modal-card">
              <h3>{copy.modalLabels.steps}</h3>
              <ol>
                {item.steps.map((step) => (
                  <li key={`${item.id}-${step}`}>{step}</li>
                ))}
              </ol>
            </div>

            <div className="tests-info-card tests-modal-card">
              <h3>{copy.modalLabels.expected}</h3>
              <p>{item.expected}</p>
            </div>

            <div className="tests-info-card warning tests-modal-card">
              <h3>{copy.modalLabels.bugs}</h3>
              <p>{item.bugs}</p>
            </div>

            <div className="tests-info-card tests-modal-card">
              <h3>{copy.modalLabels.automated}</h3>
              <p>{item.automated}</p>
            </div>

            <div className="tests-info-card note tests-modal-card">
              <h3>{copy.modalLabels.note}</h3>
              <p>{item.note}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TestHub() {
  const { language } = useTranslation();
  const copy = pageCopy[language] || pageCopy.es;
  const examples = useMemo(() => testExamples, []);
  const [selectedFamily, setSelectedFamily] = useState("all");
  const [selectedAutomation, setSelectedAutomation] = useState("all");
  const [selectedExample, setSelectedExample] = useState(null);
  const [suggestionText, setSuggestionText] = useState("");

  useEffect(() => {
    if (!selectedExample) {
      document.body.style.overflow = "";
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedExample]);

  const filteredExamples = examples.filter((item) => {
    const familyOk = selectedFamily === "all" || item.family === selectedFamily;
    const automationOk = selectedAutomation === "all" || item.automation === selectedAutomation;
    return familyOk && automationOk;
  });

  const suggestionMailHref = `mailto:diego.sanchez@theqahub.es?subject=${encodeURIComponent(copy.mailSubject)}&body=${encodeURIComponent(
    `${copy.mailFallback}\n\n${suggestionText || ""}`.trim()
  )}`;

  return (
    <div className="tests-hub-page">
      <SeoHead
        title="Tests: guia de testing con ejemplos reales | TheQAHub"
        description="Guia practica para entender tipos de testing, casos de prueba y criterio QA con ejemplos reales y clasificacion profesional."
        canonicalPath="/test-hub"
      />
      <section className="tests-hero">
        <div className="tests-hero-copy">
          <p className="tests-kicker">{copy.kicker}</p>
          <h1>{copy.title}</h1>
          <p>{copy.intro}</p>
        </div>

        <div className="tests-stats">
          {copy.stats.map((item) => (
            <article key={item.label} className="tests-stat-card">
              <strong>{item.value}</strong>
              <span>{item.label}</span>
            </article>
          ))}
        </div>
      </section>

      <section className="tests-explain">
        <p className="tests-section-kicker">{copy.guideTitle}</p>
        <div className="tests-explain-grid">
          {copy.guide.map((item) => (
            <article key={item.title} className="tests-panel">
              <h2>{item.title}</h2>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="tests-taxonomy">
        <div className="tests-library-head">
          <p className="tests-section-kicker">{copy.taxonomyTitle}</p>
          <span>{copy.taxonomyIntro}</span>
        </div>

        <div className="tests-taxonomy-grid">
          {copy.families.map((family) => (
            <article key={family.name} className="tests-panel tests-family-panel">
              <div className="tests-family-head">
                <div>
                  <h3>{family.name}</h3>
                  <p>{family.description}</p>
                </div>
                <span>{family.count}</span>
              </div>

              <ul>
                {family.items.map((item) => (
                  <li key={`${family.name}-${item}`}>{item}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="tests-playbook">
        <div className="tests-library-head">
          <p className="tests-section-kicker">{copy.playbookTitle}</p>
        </div>

        <div className="tests-playbook-grid">
          {copy.playbook.map((item, index) => (
            <article key={item.title} className="tests-playbook-card">
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="tests-library">
        <div className="tests-library-head">
          <p className="tests-section-kicker">{copy.examplesTitle}</p>
          <span>{copy.examplesIntro}</span>
        </div>

        <div className="tests-filters">
          <div>
            <span>{copy.filterFamily}</span>
            <div className="tests-chip-row">
              <button
                type="button"
                className={selectedFamily === "all" ? "active" : ""}
                onClick={() => setSelectedFamily("all")}
              >
                {copy.all}
              </button>
              {(familyOptions[language] || familyOptions.es).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={selectedFamily === option.value ? "active" : ""}
                  onClick={() => setSelectedFamily(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span>{copy.filterAutomation}</span>
            <div className="tests-chip-row">
              <button
                type="button"
                className={selectedAutomation === "all" ? "active" : ""}
                onClick={() => setSelectedAutomation("all")}
              >
                {copy.allAutomation}
              </button>
              {(automationOptions[language] || automationOptions.es).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={selectedAutomation === option.value ? "active" : ""}
                  onClick={() => setSelectedAutomation(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="tests-grid">
          {filteredExamples.map((item) => (
            <article
              key={item.id}
              className="tests-card tests-card-clickable"
              role="button"
              tabIndex={0}
              onClick={() => setSelectedExample(item)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setSelectedExample(item);
                }
              }}
            >
              <div className="tests-card-top">
                <span className="tests-tag">{item.tag}</span>
                <span className="tests-automation">{item.automation}</span>
              </div>

              <h3>{item.name}</h3>
              <p>{item.goal}</p>

              <dl>
                <div>
                  <dt>{copy.fields.goal}</dt>
                  <dd>{item.goal}</dd>
                </div>
                <div>
                  <dt>{copy.fields.caseText}</dt>
                  <dd>{item.caseText}</dd>
                </div>
                <div>
                  <dt>{copy.fields.expected}</dt>
                  <dd>{item.expected}</dd>
                </div>
                <div>
                  <dt>{copy.fields.focus}</dt>
                  <dd>{item.focus}</dd>
                </div>
              </dl>

              <button
                type="button"
                className="tests-open"
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectedExample(item);
                }}
              >
                {copy.open}
              </button>
            </article>
          ))}
        </div>
      </section>

      <section className="tests-cta">
        <article className="tests-cta-card">
          <div>
            <p className="tests-section-kicker">TheQAHub Path</p>
            <h2>{copy.ctaTitle}</h2>
            <p>{copy.ctaText}</p>
          </div>
          <Link to="/bugs" className="tests-cta-link">
            {copy.ctaButton}
          </Link>
        </article>
      </section>

      <section className="tests-suggest">
        <div className="tests-suggest-copy">
          <p className="tests-section-kicker">{copy.suggestTitle}</p>
          <span>{copy.suggestText}</span>
        </div>

        <div className="tests-suggest-card">
          <label htmlFor="tests-suggestion">{copy.suggestionLabel}</label>
          <textarea
            id="tests-suggestion"
            value={suggestionText}
            onChange={(event) => setSuggestionText(event.target.value)}
            placeholder={copy.suggestionPlaceholder}
            rows={6}
          />
          <div className="tests-suggest-actions">
            <a href={suggestionMailHref} className="tests-suggest-link tests-suggest-link-primary">
              {copy.sendMail}
            </a>
            <Link to="/contacto" className="tests-suggest-link">
              {copy.contactCta}
            </Link>
          </div>
        </div>
      </section>

      {selectedExample && (
        <TestExampleModal
          item={selectedExample}
          copy={copy}
          onClose={() => setSelectedExample(null)}
        />
      )}
    </div>
  );
}

export default TestHub;
